import {Component, EventEmitter, Inject, Input, OnInit, Output, ViewChild} from '@angular/core';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {LastModifiedCardUtils} from '@application/helper/last-modified-card-utils';
import {AsyncUniqueValidator} from '@application/validators/async-unique-validator';
import {HorizontalPositionOfColorCreelPosition} from '@domain/textile-data/color-set/horizontal-position-of-color-creel-position';
import {HorizontalPositionOfGroupCreelPosition} from '@domain/textile-data/colored-yarn-set/horizontal-position-of-group-creel-position';
import {CreelPosition} from '@domain/textile-data/creel/creel-position';
import {HorizontalPositionOfCreelPosition} from '@domain/textile-data/creel/horizontal-position-of-creel-position';
import {COLORS, Colors} from '@infrastructure/http/color/colors';
import {GridSelectPreviewCellRendererParams} from '@presentation/pages/textile-data/material-set/add/grid-select-preview/grid-select-preview-cell-renderer-params';
import {GridSelectPreviewComponent} from '@presentation/pages/textile-data/material-set/add/grid-select-preview/grid-select-preview.component';
import {
  BaseComponent,
  ColDefBuilderFactoryService,
  Color,
  ColorPickerPopupService,
  FilterComponent,
  GridOptionsBuilderFactoryService,
  LoadingCellOverlayComponent,
  NoDataOverlayComponentParams
} from '@vdw/angular-component-library';
import {AgGridAngular} from 'ag-grid-angular';
import {ColDef, DndSourceOnRowDragParams, GridOptions, GridReadyEvent, IServerSideDatasource} from 'ag-grid-community';
import {each, filter, flatMap, isEmpty, isEqual, isNil, isUndefined, remove, size} from 'lodash-es';
import {Observable, of, Subject} from 'rxjs';
import {filter as rxJsFilter, map, switchMap, takeUntil, tap} from 'rxjs/operators';

@Component({
  selector: 'app-select-color',
  templateUrl: './select-color.component.html',
  styleUrls: ['./select-color.component.scss']
})
export class SelectColorComponent extends BaseComponent implements OnInit {
  @Input() public creelPositions: CreelPosition[];
  @Output() public creelPositionHasBeenChanged: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('filterForListOfColors') public filterForListOfColors: FilterComponent;
  @ViewChild('colorsGrid') public grid!: AgGridAngular;

  public gridOptionsForListOfColors: GridOptions;
  public loadingForTheFirstTime: boolean = true;
  public readonly COLOR_EDIT_PERMISSION = LastModifiedCardUtils.getPermissionToModifyItems('color');

  private filterText: string;
  private searchSubject = new Subject<string>();
  private maxFetchPerSlice = 20;

  public constructor(
    @Inject(COLORS) private colors: Colors,
    private readonly colorPicker: ColorPickerPopupService,
    private readonly gridOptionsBuilderFactoryService: GridOptionsBuilderFactoryService,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.initialiseGridOptions();
  }

  public filterColors(searchValue: string): void {
    this.searchSubject.next(searchValue);
  }

  public createColor(): void {
    this.colorPicker
      .showColorPickerPopup(() => AsyncUniqueValidator.createValidator(this.colors, null))
      .pipe(
        takeUntil(this.unSubscribeOnViewDestroy),
        rxJsFilter((color: Color) => !isUndefined(color)),
        switchMap((color: Color) => this.saveColor(color))
      )
      .subscribe(() => {
        this.grid?.api?.refreshServerSide();
        this.filterForListOfColors.filterChanged(null);
      });
  }

  public addColorToCreelPosition(color: Color): void {
    each(this.creelPositions, (creelPosition: CreelPosition) => {
      creelPosition.horizontalPositionsOfCreelPosition.unshift(new HorizontalPositionOfColorCreelPosition(1, color));
    });

    this.creelPositionHasBeenChanged.emit();
  }

  public removeColorFromCreelPosition(color: Color): void {
    each(this.creelPositions, (creelPosition: CreelPosition) => {
      this.removeColorFromHorizontalPositions(color, creelPosition.horizontalPositionsOfCreelPosition);
    });

    this.creelPositionHasBeenChanged.emit();
  }

  public hasAlreadyAddedColor(color: Color): boolean {
    const colors = flatMap(this.creelPositions, (creelPosition: CreelPosition) => creelPosition.getUniqueItems());
    return isEqual(size(filter(colors, ['name', color.name])), size(this.creelPositions));
  }

  public hasFilterActive(): boolean {
    return !isNil(this.filterText) && !isEqual(this.filterText, '');
  }

  public canShowControls(): boolean {
    return !isEmpty(this.creelPositions);
  }

  private initialiseGridOptions(): void {
    this.gridOptionsForListOfColors = this.gridOptionsBuilderFactoryService
      .getBuilder(this.getColumnDefsForListOfColors(), GridIdentifier.SELECT_COLOR)
      .withoutColumnMenu()
      .withNoRowsOverlay(this.getNoRowsOverlayTitleParameters())
      .withServerSideDataSource(this.maxFetchPerSlice, this.getServerSideDataSource())
      .withLoadingCellRenderer('customLoadingCellRenderer')
      .withComponents({
        customLoadingCellRenderer: LoadingCellOverlayComponent
      })
      .withOnGridReady((params: GridReadyEvent) => GridSelectPreviewComponent.onGridReady(params, params.api, this.searchSubject, this.unSubscribeOnViewDestroy))
      .build();
  }

  private getColumnDefsForListOfColors(): ColDef[] {
    return [
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('name')
        .withDndSource()
        .withDndSourceOnRowDrag((params: DndSourceOnRowDragParams) => this.dragStart(params))
        .withCellRenderer(GridSelectPreviewComponent, {
          creelPositions: this.creelPositions,
          canShowControls: () => this.canShowControls(),
          hasAlreadyAddedItem: (color: Color) => this.hasAlreadyAddedColor(color),
          subtractClick: (color: Color) => this.removeColorFromCreelPosition(color),
          addClick: (color: Color) => this.addColorToCreelPosition(color),
          isColor: true
        } as GridSelectPreviewCellRendererParams<Color>)
        .withTextMultiFilter(of([]))
        .build()
    ];
  }

  private dragStart(params: DndSourceOnRowDragParams): void {
    const rowNode = params.rowNode;
    const event: any = params.dragEvent;
    event.dataTransfer.setData(
      'materialType',
      JSON.stringify({
        type: 'color',
        data: rowNode.data
      })
    );
  }

  private removeColorFromHorizontalPositions(color: Color, horizontalPositions: HorizontalPositionOfCreelPosition[]): void {
    remove(horizontalPositions, (horizontalPosition: HorizontalPositionOfCreelPosition) => {
      let shouldRemove = false;
      if (horizontalPosition.isColor()) {
        shouldRemove = isEqual((horizontalPosition as HorizontalPositionOfColorCreelPosition).color.id, color.id);
      } else if (horizontalPosition.isGroup()) {
        this.removeColorFromHorizontalPositions(color, (horizontalPosition as HorizontalPositionOfGroupCreelPosition).group);
      }
      return shouldRemove;
    });
  }

  private saveColor(color: Color): Observable<Color> {
    return this.colors.save(color).pipe(
      tap((id: number) => (color.id = id)),
      map(() => color)
    );
  }

  private getServerSideDataSource(): IServerSideDatasource {
    return GridSelectPreviewComponent.getServerSideDataSource<Color>(this.colors, this.unSubscribeOnViewDestroy, this.maxFetchPerSlice, () => this.onRequestCompleted());
  }

  private getNoRowsOverlayTitleParameters(): Partial<NoDataOverlayComponentParams> {
    return {
      scale: 1,
      titleKey: null,
      titleParam: 'TEXTILE_DATA.COLOR.COLOR',
      hideDescription: true
    };
  }

  private onRequestCompleted(): void {
    if (this.loadingForTheFirstTime) {
      this.loadingForTheFirstTime = false;
      this.gridOptionsForListOfColors.loadingCellRendererParams = {
        loadingForTheFirstTime: false
      };
    }
  }
}
